import type { QuoteDocument, OptionsDocument } from 'paintscout'
import find from 'lodash/find'
import { calculate as quoteCalculate } from '../../../../calculator'
import { mergeLineItem, toUpdateableLineItem } from '../../line-items'
import { getLineItemOptions } from '../../../options'
import type { LineItemOption } from '../../../options'

/**
 * Replace line items with their updated version from option if it exists, and updated products
 * from options if they exist
 * @returns updated quote with line items updated, optionally calculated
 */
export function replaceLineItems({
  quote,
  options,
  calculate = true
}: {
  quote: Partial<QuoteDocument>
  options: OptionsDocument
  calculate?: boolean
}): Partial<QuoteDocument> {
  const { lineItems = {} } = quote

  const lineItemOptions = getLineItemOptions({ options })
  const updatedQuote = Object.keys(lineItems).reduce((acc, key) => {
    const lineItem = quote.lineItems[key]
    const lineItemOption = find<LineItemOption>(lineItemOptions, { name: lineItem.optionKey })

    if (!lineItemOption) return acc

    // if we find a linked option for line item then use that
    const updateableLineItem = toUpdateableLineItem({ lineItemOption, quoteType: lineItem?.quoteType ?? 'all' })
    const mergedLineItem = mergeLineItem({
      key,
      originalItem: lineItem,
      updateableLineItem: {
        ...updateableLineItem,
        files: lineItem.files // Files get rekeyed, so can not support legacy users w/ files allowed on line item options
      }
    })

    return {
      ...acc,
      lineItems: {
        ...acc.lineItems,
        [key]: mergedLineItem
      }
    } as QuoteDocument
  }, quote as QuoteDocument)

  return calculate ? quoteCalculate(updatedQuote, options) : updatedQuote
}
